import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation.jsx';
import Footer from '../components/Footer.jsx';

function BlogBreakingTheCycle() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 via-white to-warm-50">
      <Navigation variant="soft-brown" scrolled={true} />
      
      {/* Article Header */}
      <section className="px-4 sm:px-6 lg:px-8 pt-32 pb-12">
        <div className="max-w-4xl mx-auto">
          <Link to="/blog" className="inline-flex items-center text-sage-600 hover:text-sage-800 mb-8 transition-colors">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Blog
          </Link>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-light text-sage-800 mb-6">
            Breaking the Cycle: End Generational Trauma in Your Lifetime
          </h1>
          
          <div className="flex items-center text-gray-600 text-sm mb-8">
            <span>By Yasi Philipos</span>
            <span className="mx-3">•</span>
            <span>6 min read</span>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg">
            <p className="text-xl text-gray-700 mb-8 leading-relaxed">
              You don't have to carry what your parents couldn't name. Generational trauma can end with you—through conscious awareness, nervous system healing, and the courage to break old patterns.
            </p>

            <h2 className="text-3xl font-serif font-light text-sage-800 mt-12 mb-6">What Is Generational Trauma?</h2>
            <p className="text-gray-700 mb-6 leading-relaxed">
              Generational trauma—also called intergenerational or transgenerational trauma—refers to the way trauma gets passed down through families. It's not just about stories told at the dinner table. It's embedded in our nervous systems, our attachment styles, and our unconscious beliefs about safety, love, and belonging.
            </p>
            <p className="text-gray-700 mb-6 leading-relaxed">
              Research shows that trauma can literally change gene expression through epigenetics. When your grandmother experienced war, displacement, or violence, her body's response to stress may have shaped how your parent's nervous system developed—and in turn, how yours did too.
            </p>

            <h2 className="text-3xl font-serif font-light text-sage-800 mt-12 mb-6">How Trauma Shows Up Across Generations</h2>
            <p className="text-gray-700 mb-6 leading-relaxed">
              Generational trauma often shows up as patterns that feel confusing or inexplicable:
            </p>
            <ul className="space-y-4 my-8">
              <li className="flex items-start gap-3">
                <span className="text-sage-600 mt-1">•</span>
                <span className="text-gray-700"><strong>Chronic hypervigilance</strong> — You're always scanning for danger, even in safe spaces</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-sage-600 mt-1">•</span>
                <span className="text-gray-700"><strong>Emotional suppression</strong> — Feelings are "too much" or "unsafe" to express</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-sage-600 mt-1">•</span>
                <span className="text-gray-700"><strong>Fear of abandonment</strong> — Deep anxiety about being left, even in secure relationships</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-sage-600 mt-1">•</span>
                <span className="text-gray-700"><strong>Shame-based beliefs</strong> — "I'm too much," "I'm not enough," "I don't belong"</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-sage-600 mt-1">•</span>
                <span className="text-gray-700"><strong>Perfectionism and people-pleasing</strong> — Trying to earn safety through being "good enough"</span>
              </li>
            </ul>

            <h2 className="text-3xl font-serif font-light text-sage-800 mt-12 mb-6">Why It Matters That You're Aware</h2>
            <p className="text-gray-700 mb-6 leading-relaxed">
              The moment you recognize a pattern, you create space for something new. Awareness itself is revolutionary. You're no longer unconsciously repeating what was done to you—you're making a choice.
            </p>
            <p className="text-gray-700 mb-6 leading-relaxed">
              Breaking the cycle doesn't mean you have to "fix" your family or confront every hurt. It means you choose healing for yourself. You learn to listen to your body. You practice setting boundaries without guilt. You allow yourself to feel what was never safe to feel before.
            </p>

            <h2 className="text-3xl font-serif font-light text-sage-800 mt-12 mb-6">Practical Steps to Break the Cycle</h2>
            
            <h3 className="text-2xl font-serif font-light text-sage-700 mt-8 mb-4">1. Regulate Your Nervous System</h3>
            <p className="text-gray-700 mb-6 leading-relaxed">
              Healing generational trauma isn't just cognitive—it's somatic. Your body holds the patterns, so healing must include the body. Practices like somatic therapy, breathwork, and gentle movement help you build a new relationship with safety.
            </p>

            <h3 className="text-2xl font-serif font-light text-sage-700 mt-8 mb-4">2. Name What Wasn't Named</h3>
            <p className="text-gray-700 mb-6 leading-relaxed">
              Give language to what your family couldn't speak about. Was there war, displacement, abuse, addiction? Naming these realities with compassion helps you separate what's yours to carry from what's not.
            </p>

            <h3 className="text-2xl font-serif font-light text-sage-700 mt-8 mb-4">3. Practice Self-Compassion</h3>
            <p className="text-gray-700 mb-6 leading-relaxed">
              Breaking cycles requires immense courage. You're doing something your ancestors couldn't. That doesn't make them bad—it makes you brave. Practice kindness toward yourself as you navigate this work.
            </p>

            <h3 className="text-2xl font-serif font-light text-sage-700 mt-8 mb-4">4. Seek Trauma-Informed Support</h3>
            <p className="text-gray-700 mb-6 leading-relaxed">
              Working with a therapist who understands generational trauma, attachment, and nervous system healing can make all the difference. This work is hard to do alone.
            </p>

            <h3 className="text-2xl font-serif font-light text-sage-700 mt-8 mb-4">5. Model a New Way Forward</h3>
            <p className="text-gray-700 mb-6 leading-relaxed">
              The most powerful thing you can do is live differently. When you regulate your nervous system, set boundaries, and choose presence over people-pleasing, you're modeling a new way of being—for yourself, your family, and future generations.
            </p>

            <div className="bg-gradient-to-r from-sage-50 to-warm-50 border-l-4 border-sage-600 p-8 my-12 rounded-xl">
              <p className="text-lg text-sage-900 italic leading-relaxed">
                "You are not responsible for the trauma you inherited, but you are responsible for healing it. And in healing, you give your children—and your children's children—a different inheritance: one of wholeness, safety, and love."
              </p>
            </div>

            <h2 className="text-3xl font-serif font-light text-sage-800 mt-12 mb-6">You Can End the Cycle</h2>
            <p className="text-gray-700 mb-6 leading-relaxed">
              Generational trauma ends when someone has the courage to say: "This stops with me." That someone can be you.
            </p>
            <p className="text-gray-700 mb-6 leading-relaxed">
              You don't need to be perfect. You don't need to have it all figured out. You just need to be willing to do your own healing work—imperfectly, courageously, and with compassion.
            </p>
            <p className="text-lg font-medium text-sage-800 mb-8">
              The cycle can end in your lifetime. And it starts with you.
            </p>

            {/* CTA Section */}
            <div className="mt-16 p-8 bg-gradient-to-br from-warm-50 to-sage-50 rounded-2xl border border-warm-200">
              <h3 className="text-2xl font-serif font-light text-sage-800 mb-4">Ready to Break the Cycle?</h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                If you're ready to heal generational patterns and build a new legacy, I'd love to support you.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/services/individual-sessions" 
                  className="inline-block bg-sage-600 text-white px-8 py-3 rounded-lg hover:bg-sage-700 transition-colors text-center font-medium"
                >
                  Explore Individual Sessions
                </Link>
                <Link 
                  to="/services/group-coaching" 
                  className="inline-block border-2 border-sage-600 text-sage-700 px-8 py-3 rounded-lg hover:bg-sage-50 transition-colors text-center font-medium"
                >
                  Join Group Coaching
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default BlogBreakingTheCycle;
